<div class="space-y-6">
  <header>
    <h1 class="text-title-sm font-bold text-gray-800 dark:text-white/90 sm:text-title-md">Assignations de rôles</h1>
    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
      Recherche un utilisateur, puis coche/décoche les rôles à lui appliquer.
      Identique au panneau « Gérer les rôles » de la fiche utilisateur, accessible ici sans naviguer.
    </p>
  </header>

  <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
    <label for="user-search" class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-400">
      Rechercher un utilisateur (nom, identifiant, email…)
    </label>
    <input
      id="user-search"
      type="text"
      [formControl]="searchCtrl"
      placeholder="Au moins 2 caractères"
      class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
    />

    @if (searchError(); as msg) {
      <div class="mt-3 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-xs text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">{{ msg }}</div>
    }

    @if (searchLoading()) {
      <p class="mt-3 text-xs text-gray-500 dark:text-gray-400">Recherche…</p>
    } @else if (searchResults().length > 0) {
      <ul class="mt-3 divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-gray-800 dark:border-gray-800">
        @for (u of searchResults(); track u.id) {
          <li>
            <button
              type="button"
              (click)="selectUser(u)"
              class="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-gray-50 dark:hover:bg-white/[0.02]"
            >
              @if (u.avatarUrl) {
                <img [src]="u.avatarUrl" class="h-9 w-9 rounded-full object-cover" alt="" />
              } @else {
                <span class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">{{ initials(u) }}</span>
              }
              <span class="min-w-0 flex-1">
                <span class="block truncate text-sm font-medium text-gray-800 dark:text-white/90">{{ fullName(u) }}</span>
                <span class="block truncate text-xs text-gray-500 dark:text-gray-400">&#64;{{ u.username }} · {{ u.email || '—' }}</span>
              </span>
            </button>
          </li>
        }
      </ul>
    }
  </section>

  @if (selectedUser(); as u) {
    <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center gap-3">
          @if (u.avatarUrl) {
            <img [src]="u.avatarUrl" class="h-12 w-12 rounded-full object-cover" alt="" />
          } @else {
            <span class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">{{ initials(u) }}</span>
          }
          <div>
            <p class="text-base font-semibold text-gray-800 dark:text-white/90">{{ fullName(u) }}</p>
            <p class="text-xs text-gray-500 dark:text-gray-400">&#64;{{ u.username }} · {{ u.email || '—' }}</p>
          </div>
        </div>
        <button
          type="button"
          (click)="clearSelection()"
          class="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
        >
          Changer d'utilisateur
        </button>
      </div>

      <div class="mt-5">
        <input
          type="text"
          [formControl]="rolesFilterCtrl"
          placeholder="Filtrer les rôles…"
          class="h-10 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
        />

        <div class="mt-3 max-h-[55vh] overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-800">
          @if (rolesLoading()) {
            <p class="px-4 py-8 text-center text-xs text-gray-500 dark:text-gray-400">Chargement des rôles…</p>
          } @else if (visibleRoles().length === 0) {
            <p class="px-4 py-8 text-center text-xs text-gray-500 dark:text-gray-400">Aucun rôle.</p>
          } @else {
            <ul class="divide-y divide-gray-200 dark:divide-gray-800">
              @for (r of visibleRoles(); track r.name) {
                <li>
                  <label class="flex cursor-pointer items-start gap-3 px-4 py-3 transition hover:bg-gray-50 dark:hover:bg-white/[0.02]">
                    <input
                      type="checkbox"
                      [checked]="isRoleSelected(r.name)"
                      (change)="toggleRole(r.name)"
                      class="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500/20 dark:border-gray-600"
                    />
                    <span class="min-w-0 flex-1">
                      <span class="flex items-center gap-1.5">
                        <span class="font-mono text-sm text-gray-800 dark:text-white/90">{{ r.name }}</span>
                        @if (isBusinessRole(r.name)) {
                          <span class="rounded-full bg-brand-50 px-1.5 py-0.5 text-[9px] font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">métier</span>
                        } @else {
                          <span class="rounded-full bg-gray-100 px-1.5 py-0.5 text-[9px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">système</span>
                        }
                      </span>
                      @if (r.description) {
                        <span class="block text-xs text-gray-500 dark:text-gray-400">{{ r.description }}</span>
                      }
                    </span>
                  </label>
                </li>
              }
            </ul>
          }
        </div>
      </div>

      @if (serverError(); as msg) {
        <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">{{ msg }}</div>
      }
      @if (successMessage(); as msg) {
        <div class="mt-4 rounded-lg border border-success-200 bg-success-50 px-3 py-2 text-sm text-success-700 dark:border-success-800 dark:bg-success-500/10 dark:text-success-400">{{ msg }}</div>
      }

      <div class="mt-5 flex items-center justify-between">
        <span class="text-xs text-gray-500 dark:text-gray-400">
          {{ selectedRoles().size }} rôle(s) coché(s)
          @if (rolesDirty()) { <span class="ml-1 text-warning-700 dark:text-warning-400">• modifications non enregistrées</span> }
        </span>
        <button
          type="button"
          (click)="save()"
          [disabled]="submitting() || !rolesDirty()"
          class="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-medium text-white shadow-theme-xs transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-brand-300"
        >
          @if (submitting()) {
            <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-opacity="0.25" />
              <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
            </svg>
            Enregistrement…
          } @else { Enregistrer les modifications }
        </button>
      </div>
    </section>
  } @else if (!searchLoading() && searchResults().length === 0 && searchCtrl.value) {
    <p class="text-center text-xs text-gray-500 dark:text-gray-400">Aucun utilisateur ne correspond à « {{ searchCtrl.value }} ».</p>
  } @else if (!selectedUser()) {
    <p class="text-center text-xs text-gray-500 dark:text-gray-400">Sélectionne un utilisateur ci-dessus pour gérer ses rôles.</p>
  }
</div>
